import { motion, AnimatePresence } from "framer-motion"
import Image from "next/image"
import React, { useContext, useEffect } from "react"
import useDimensions from "react-cool-dimensions"

import { ContentfulRichText } from "@/helpers/ContentfulRichText"
import { mapContentfulToButtonProps } from "@/helpers/utils"

import CloseIcon from "../../../../../public/icons/modal-close-any-color.svg"
import AppContext from "../../AppContext"
import Button from "../../Button"
import PageGrid from "../../PageGrid"
import VideoPlayer from "../../VideoPlayer"

type SpotlightModalProps = {
  modalOpen: boolean
}

const ModalBackdrop: React.FC = () => {
  const context = useContext(AppContext)
  const closeModal = () => {
    context.setImpactReportSpotlightModal(false)
  }

  return (
    <motion.div
      className="fixed inset-0 w-full h-screen z-[75] bg-gray-90"
      onClick={() => {
        closeModal()
      }}
    />
  )
}

const SpotlightModal: React.FC<SpotlightModalProps> = ({ modalOpen }) => {
  const context = useContext(AppContext)
  const closeModal = () => {
    context.setImpactReportSpotlightModal(false)
  }

  const contentData = context.impactReportSpotlightModal.contentData || {}
  const detailsModal = (contentData as any).detailsModal || {}

  const { observe: modalHeader } = useDimensions({
    useBorderBoxSize: true,
    polyfill: ResizeObserver,
  })

  useEffect(() => {
    if (modalOpen) {
      document.body.classList.add("body-modal-open")
    } else {
      document.body.classList.remove("body-modal-open")
    }
    // Cleanup function to ensure class is removed if component is unmounted
    return () => {
      document.body.classList.remove("body-modal-open")
    }
  }, [modalOpen])

  if (!detailsModal.title) return null

  return (
    <AnimatePresence>
      {modalOpen && (
        <>
          <ModalBackdrop />
          <section className="spotlight-modal fixed inset-0 z-[75] lg:py-18 md:py-8 py-4 overflow-auto">
            <PageGrid ref={modalHeader}>
              {/* Title and close btn header starts here */}
              <div className="flex items-center justify-between px-4 mb-6 md:mb-4 lg:mb-8 col-span-full">
                <h2 className="heading2 md:heading1 lg:headline5 text-gray-2.5 py-4">
                  {detailsModal.title}
                </h2>
                <button
                  aria-label="Close modal"
                  type="button"
                  className="w-[52px] h-[52px] flex items-center justify-center"
                  onClick={() => {
                    closeModal()
                  }}
                >
                  <CloseIcon className="w-5 h-[18.54px] cursor-pointer text-gray-0" />
                </button>
              </div>
              {/* End Title and close btn header */}
            </PageGrid>
            <PageGrid className="overflow-y-auto h-[var(--modalContentHeight)]">
              {/* Img and copy OR Video and copy starts here */}
              <div className="col-span-full md:col-span-2 lg:col-span-7">
                {/* Hero content: video or image */}
                {detailsModal.video?.videoLink ? (
                  <VideoPlayer
                    wistiaVideo={detailsModal.video.wistiaVideo}
                    videoURL={detailsModal.video.videoLink}
                    title={detailsModal.video.videoName || ""}
                    image={detailsModal.video.videoCover?.url}
                  />
                ) : (
                  detailsModal.heroAsset && (
                    <Image
                      width={978}
                      height={458}
                      sizes="(min-width: 978px) 978px, 100vw"
                      src={detailsModal.heroAsset.url}
                      className="mb-4 lg:mb-6 object-cover"
                      loading="eager"
                      alt={detailsModal.heroAsset.title || ""}
                    />
                  )
                )}

                {/* Description text */}
                <div className="p-4 text-gray-2.5 body3 description-wrapper">
                  {detailsModal.description?.json && (
                    <ContentfulRichText
                      document={detailsModal.description.json}
                    />
                  )}
                </div>

                {/* Button if available */}
                {detailsModal.button && (
                  <div className="p-4 pt-0">
                    <Button
                      {...mapContentfulToButtonProps(detailsModal.button)}
                      target="_blank"
                    />
                  </div>
                )}
              </div>
              {/* End Img and copy OR Video and copy */}

              {/* Number callout and logo column starts here */}
              <div className="col-span-full md:col-span-2 lg:col-span-5">
                {detailsModal.statsCollection?.items &&
                  detailsModal.statsCollection.items.map((stat, index) => (
                    <div
                      key={stat?.title || `stat-${index}`}
                      className="text-gray-2.5 p-4"
                    >
                      <p className="headline5">{stat?.title}</p>
                      <div className="body4">
                        {stat?.description?.json && (
                          <ContentfulRichText
                            document={stat.description.json}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                {/* Logo display */}
                {detailsModal.logosCollection?.items &&
                  detailsModal.logosCollection.items.length > 0 && (
                    <div className="mt-4">
                      {detailsModal.logosCollection.items.map((logo, index) => {
                        const logoSrc = logo?.whiteLogo?.url
                        if (!logoSrc) return null
                        return (
                          <Image
                            key={logoSrc || `logo-${index}`}
                            src={logoSrc}
                            width={100}
                            height={100}
                            loading="eager"
                            alt=""
                            className="mt-4"
                          />
                        )
                      })}
                    </div>
                  )}
              </div>
              {/* End Number callout and logo column */}
            </PageGrid>
          </section>
        </>
      )}
    </AnimatePresence>
  )
}

export default SpotlightModal
